import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { FormsModule } from "@angular/forms";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EnergyExportService } from "../../../../services/api/energyExport/energy-export.service";
import { DateTime } from "luxon";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { SelectModule } from "@openng/optimus-ui/select";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { MessageService } from "@openng/optimus-ui/api";

@Component({
  selector: "app-energy-export-dialog",
  standalone: true,
  imports: [ButtonComponent, DatePickerModule, FormsModule, SelectModule, TranslateModule, ProgressSpinnerModule],
  templateUrl: "./energy-export-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./energy-export-dialog.component.scss",
})
export class EnergyExportDialogComponent {
  private config = inject(DynamicDialogConfig);
  private energyExportService = inject(EnergyExportService);
  private assetId = this.config.data.assetId;
  public exportDate: Date;
  public maxDate: Date;
  private ref = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  private message = inject(MessageService);
  public selectedLanguage = this.translate.currentLang;
  public languages: DropdownOption[] = [
    { label: "common.german", value: "de" },
    { label: "common.english", value: "en" },
  ];

  public loading = signal(false);

  constructor() {
    const now = new Date();
    this.exportDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    // Last day of last month
    this.maxDate = new Date(now.getFullYear(), now.getMonth(), 0);
  }

  /**
   * Closes the energy export dialog.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Downloads the energy export report for the given asset and time range.
   * @see EnergyExportService#getEnergyReport
   */
  public startExport() {
    const startDate = DateTime.fromJSDate(this.exportDate).startOf("month");
    const endDate = DateTime.fromJSDate(this.exportDate).endOf("month");
    const currentLang = this.selectedLanguage;
    const fileNameSuffix = startDate.toFormat("MMMM_yyyy", {
      locale: currentLang,
    });
    const from = startDate.toISODate();
    const to = endDate.toISODate();
    if (!from || !to) {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_date"),
        life: 4500,
      });
      return;
    }
    // const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.loading.set(true);
    this.energyExportService.getEnergyReport(from, to, ASSET_TYPES.CUSTOMER, this.assetId, currentLang).subscribe({
      next: (pdfBlob) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Energy_Report_" + fileNameSuffix + ".pdf"; // set desired file name
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);

        this.loading.set(false);
        this.ref.close(true);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }
}
